import { Api } from '@monorepo/shared/apiClient';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type KeyParams = Api.FetchQueryOriginStatusParams;
const KEY_START = 'queryOriginStatus';

export const createQueryKey = ({
  organizationId,
  originId,
  projectId,
}: KeyParams) => [KEY_START, organizationId, originId, projectId] as const;

type Fetcher = Api.DataHookQueryFn<
  typeof createQueryKey,
  typeof Api.fetchQueryOriginStatus
>;

type UseQueryOriginStatusParams = Partial<Api.FetchQueryOriginStatusParams> & {
  config?: QueryConfig<Api.FetchQueryOriginStatusResponse, Api.ErrorResponse>;
};

const fetcher: Fetcher = (
  _: string,
  organizationId: string,
  originId: string,
  projectId: string,
) => Api.fetchQueryOriginStatus({ organizationId, originId, projectId });

export const useQueryOriginStatus = ({
  config: inputConfig,
  organizationId,
  projectId,
  originId,
}: UseQueryOriginStatusParams) => {
  const isEnabled = !!organizationId && !!projectId && !!originId;
  const config = getQueryConfig<Api.FetchQueryOriginStatusResponse>(
    inputConfig,
    isEnabled,
  );
  const key = isEnabled
    ? createQueryKey({ organizationId, projectId, originId })
    : undefined;
  const { data: originStatus, ...queryInfo } = useQuery(key, fetcher, config);
  return {
    originStatus,
    ...queryInfo,
  };
};

type InvalidateCacheParams = Partial<KeyParams>;

export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([
      KEY_START,
      keyParams.organizationId,
      keyParams.originId,
      keyParams.projectId,
    ]),
  );
}

// we don't subscribe to any cache registers because this will be invalidated via push notification
